'use client';

import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { useTrackingStore } from '@/stores';

interface WebSocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  usingRestFallback: boolean;
  connect: () => void;
  disconnect: () => void;
  subscribe: <T = unknown>(event: string, callback: (data: T) => void) => void;
  unsubscribe: <T = unknown>(event: string, callback?: (data: T) => void) => void;
  emit: <T = unknown>(event: string, data: T) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: React.ReactNode;
  url?: string;
  autoConnect?: boolean;
}

// Global singleton for connection pooling (client-side only)
// Use process.browser check to prevent SSR issues
interface GlobalSocketState {
  socket: Socket | null;
  refCount: number;
}

declare global {
  interface Window {
    __globalSocket?: GlobalSocketState;
  }
}

const getGlobalSocket = (): GlobalSocketState => {
  if (typeof window !== 'undefined') {
    if (!window.__globalSocket) {
      window.__globalSocket = { socket: null, refCount: 0 };
    }
    return window.__globalSocket;
  }
  return { socket: null, refCount: 0 };
};

export function WebSocketProvider({
  children,
  url,
  autoConnect = true
}: WebSocketProviderProps) {
  const socketRef = useRef<Socket | null>(null);
  const reconnectCountRef = useRef(0);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingRestFallback, setUsingRestFallback] = useState(false);

  const { addConnection, removeConnection } = useTrackingStore();

  // Resolve WebSocket URL: explicit prop > env var > derive from hostname
  const resolvedWsUrl = React.useMemo(() => {
    if (url) return url;
    if (typeof window === 'undefined') return 'ws://localhost:3001';
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // In production (non-localhost), nginx proxies /socket.io/ to the API
    // In dev (localhost), connect directly to the API on port 3001
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return `${protocol}://${window.location.hostname}:3001`;
    }
    return `${protocol}://${window.location.hostname}`;
  }, [url]);

  const connect = () => {
    // CONNECTION POOLING: Reuse existing global socket if available
    const globalState = getGlobalSocket();

    // Guard 1: Already connected - just adopt the existing socket
    if (globalState.socket?.connected) {
      socketRef.current = globalState.socket;
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
      setUsingRestFallback(false);
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      return;
    }

    // Guard 2: Socket exists but is still connecting - don't create a duplicate
    if (globalState.socket) {
      socketRef.current = globalState.socket;
      return;
    }

    setIsConnecting(true);
    setError(null);
    setUsingRestFallback(false);

    // 10-SECOND TIMEOUT: If WebSocket doesn't connect within 10 seconds, fall back to REST
    connectionTimeoutRef.current = setTimeout(() => {
      if (socketRef.current && !socketRef.current.connected) {
        // WebSocket failed to connect within 10 seconds - close and use REST fallback
        if (process.env.NODE_ENV === 'development') {
          console.warn('[WebSocket] Connection timeout (10s) - falling back to REST API');
        }
        socketRef.current.disconnect();
        socketRef.current = null;
        socketRef.current = null;
        setIsConnecting(false);
        setIsConnected(false);
        setUsingRestFallback(true);
        setError('WebSocket unavailable - using REST API fallback');
      }
    }, 3000);

    // Create new socket - websocket only (polling disabled to prevent CSP spam)
    const socket = io(resolvedWsUrl, {
      transports: ['websocket'], // Only use websocket, no polling fallback
      timeout: 10000,
      forceNew: false,
      reconnection: false, // We handle reconnection ourselves via 3s timeout
      auth: {
        token: typeof localStorage !== 'undefined' ? localStorage.getItem('joyous_admin_token') || null : null
      },
      withCredentials: true
    });

    socketRef.current = socket;

    // Store in global state for connection pooling (client-side only)
    if (typeof window !== 'undefined') {
      const globalState = getGlobalSocket();
      globalState.socket = socket;
      globalState.refCount++;
    }

    socket.on('connect', () => {
      if (process.env.NODE_ENV === 'development') {
      }
      // Clear the connection timeout since we connected successfully
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
      setUsingRestFallback(false);
      reconnectCountRef.current = 0;
      addConnection(socket.id);
    });

    socket.on('disconnect', (reason) => {
      if (reason !== 'io client disconnect' && process.env.NODE_ENV === 'development') {
      }
      setIsConnected(false);
      setIsConnecting(false);
      if (socket.id) {
        removeConnection(socket.id);
      }

      // Auto-reconnect logic (only once, then fall back to REST)
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        return;
      }

      if (reconnectCountRef.current < 1 && !usingRestFallback) {
        setTimeout(() => {
          reconnectCountRef.current++;
          connect();
        }, 3000);
      }
    });

    socket.on('connect_error', (error) => {
      const isExpectedError = error.message.includes('ERR_CONNECTION_REFUSED') ||
                             error.message.includes('ECONNREFUSED') ||
                             error.message.includes('timeout');

      if (process.env.NODE_ENV === 'development' && !isExpectedError) {
        console.warn('⚠️  WebSocket connection error:', error.message);
      }

      setError(isExpectedError ? 'Backend server unavailable' : error.message);
      setIsConnecting(false);
      setIsConnected(false);
    });

    socket.on('error', (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ WebSocket error:', error);
      }
      setError(error.message);
    });
  };

  const disconnect = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setIsConnecting(false);
      setError(null);
    }
  };

  const subscribe = <T = unknown>(event: string, callback: (data: T) => void) => {
    if (socketRef.current) {
      const optimizedCallback = (data: T) => {
        setTimeout(() => {
          try {
            callback(data);
          } catch (error) {
            console.error(`WebSocket callback error for event ${event}:`, error);
          }
        }, 0);
      };

      socketRef.current.on(event, optimizedCallback);
      if (process.env.NODE_ENV === 'development') {
      }
    }
  };

  const unsubscribe = <T = unknown>(event: string, callback?: (data: T) => void) => {
    if (socketRef.current) {
      if (callback) {
        socketRef.current.off(event, callback);
      } else {
        socketRef.current.off(event);
      }
      if (process.env.NODE_ENV === 'development') {
      }
    }
  };

  const emit = <T = unknown>(event: string, data: T) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit(event, data);
      if (process.env.NODE_ENV === 'development') {
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('❌ Cannot emit - WebSocket not connected');
      }
      setError('Cannot emit - WebSocket not connected');
    }
  };

  useEffect(() => {
    // Only run on client-side
    if (typeof window === 'undefined') {
      return;
    }

    let mounted = true;

    if (autoConnect && !socketRef.current && mounted) {
      const timer = setTimeout(() => {
        if (mounted && !socketRef.current) {
          connect();
        }
      }, 100);

      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }

    // CRITICAL: Cleanup on unmount
    return () => {
      mounted = false;
      if (typeof window !== 'undefined') {
        const globalState = getGlobalSocket();
        if (socketRef.current && globalState.refCount <= 1) {
          socketRef.current.disconnect();
          socketRef.current = null;
          globalState.socket = null;
          globalState.refCount = 0;
          setIsConnected(false);
          setIsConnecting(false);
        } else if (globalState.refCount > 1) {
          globalState.refCount--;
        }
      }
    };
  }, [autoConnect, resolvedWsUrl]);

  const contextValue: WebSocketContextValue = {
    socket: socketRef.current,
    isConnected,
    isConnecting,
    error,
    usingRestFallback,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    emit
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}